import { useEffect, useMemo, useRef, useState } from "react";
import { addDays, addWeeks, format, startOfWeek, subWeeks } from "date-fns";
import {
  Bell,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Loader2,
  Lock,
  MoreVertical,
  Plus,
  Send,
  Unlock,
  Users,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

import {
  type CarrierAvailabilityRequest,
  type CarrierAvailabilityRequestOperator,
  type CarrierAvailabilityRow,
  type CarrierDispatchCenter,
  type CarrierOperator,
  type CarrierShiftRow,
  useCarrierAvailability,
  useCarrierDispatchCenters,
  useCarrierOperators,
  useCarrierSchedulingRequests,
  useCarrierShifts,
  useCreateAvailabilityRequest,
  useCreateCarrierShift,
  useDeleteCarrierShift,
  useRemindAvailabilityRequest,
  useUpdateAvailabilityRequestStatus,
} from "@/api/carrier";
import { DetailDrawer } from "@/components/detail-drawer";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { hasHttpStatus } from "@/lib/api-error";
import { operatorDisplayName } from "@/lib/operator-identity";
import { cn } from "@/lib/utils";

const DAYS = [
  { key: "mon", label: "Mon", aliases: ["monday"] },
  { key: "tue", label: "Tue", aliases: ["tuesday"] },
  { key: "wed", label: "Wed", aliases: ["wednesday"] },
  { key: "thu", label: "Thu", aliases: ["thursday"] },
  { key: "fri", label: "Fri", aliases: ["friday"] },
  { key: "sat", label: "Sat", aliases: ["saturday"] },
  { key: "sun", label: "Sun", aliases: ["sunday"] },
] as const;

const SHIFT_TYPES = ["MORNING", "AFTERNOON", "EVENING"] as const;
type ShiftType = (typeof SHIFT_TYPES)[number];
type ScheduledShift = CarrierShiftRow["shifts"][number];

const SHIFT_CONFIG: Record<
  ShiftType,
  {
    label: string;
    short: string;
    time: string;
    bg: string;
    bgLight: string;
    text: string;
    border: string;
    defaultStart: string;
    defaultEnd: string;
  }
> = {
  MORNING: {
    label: "Morning",
    short: "6-12",
    time: "6:00-12:00",
    bg: "bg-yellow-500",
    bgLight: "bg-yellow-50",
    text: "text-yellow-800",
    border: "border-yellow-300",
    defaultStart: "06:00",
    defaultEnd: "12:00",
  },
  AFTERNOON: {
    label: "Afternoon",
    short: "12-18",
    time: "12:00-18:00",
    bg: "bg-blue-500",
    bgLight: "bg-blue-50",
    text: "text-blue-800",
    border: "border-blue-300",
    defaultStart: "12:00",
    defaultEnd: "18:00",
  },
  EVENING: {
    label: "Evening",
    short: "18-24",
    time: "18:00-24:00",
    bg: "bg-purple-500",
    bgLight: "bg-purple-50",
    text: "text-purple-800",
    border: "border-purple-300",
    defaultStart: "18:00",
    defaultEnd: "00:00",
  },
};

export default function SchedulingScreen() {
  const [weekStartDate, setWeekStartDate] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [selectedDispatchCenterId, setSelectedDispatchCenterId] = useState("");

  const dispatchCenters = useCarrierDispatchCenters();
  const operators = useCarrierOperators({ limit: 100 });

  useEffect(() => {
    if (!dispatchCenters.data) return;
    const activeCenters = dispatchCenters.data.filter(
      (center) => center.isActive
    );
    const inScope = activeCenters.some(
      (center) => center.id === selectedDispatchCenterId
    );
    if (activeCenters.length > 0 && !inScope) {
      setSelectedDispatchCenterId(activeCenters[0].id);
    } else if (activeCenters.length === 0 && selectedDispatchCenterId) {
      setSelectedDispatchCenterId("");
    }
  }, [dispatchCenters.data, selectedDispatchCenterId]);

  const selectedDispatchCenter = dispatchCenters.data?.find(
    (center) => center.id === selectedDispatchCenterId
  );
  const dispatchCenterOperators = useMemo(
    () =>
      (operators.data?.data ?? []).filter(
        (operator) =>
          (operator.dispatchCenterId ?? operator.depot?.dispatchCenterId) ===
          selectedDispatchCenterId
      ),
    [operators.data?.data, selectedDispatchCenterId]
  );

  if (dispatchCenters.isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading scheduling"
        className="flex min-h-full items-center justify-center bg-zinc-50 p-8"
      >
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (dispatchCenters.error) {
    return (
      <ErrorMessage
        label="dispatch centers"
        forbidden={hasHttpStatus(dispatchCenters.error, 403)}
      />
    );
  }

  return (
    <div className="carrier-page">
      <PageHeader
        title="Scheduling"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:gap-3">
            <DispatchCenterSelect
              dispatchCenters={dispatchCenters.data ?? []}
              selectedDispatchCenterId={selectedDispatchCenterId}
              onDispatchCenterChange={setSelectedDispatchCenterId}
            />
            <WeekNavigator
              weekStart={weekStartDate}
              onChange={setWeekStartDate}
            />
          </div>
        }
      />

      {!selectedDispatchCenterId ? (
        <Card className="border-dashed py-12 text-center text-zinc-500">
          Select a dispatch center to view scheduling
        </Card>
      ) : (
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="w-full shrink-0 lg:w-72">
            <RequestAvailabilityCard
              weekStart={weekStartDate}
              dispatchCenterId={selectedDispatchCenterId}
              dispatchCenterName={
                selectedDispatchCenter?.name ?? "Dispatch center"
              }
              operators={dispatchCenterOperators}
              operatorsLoading={operators.isLoading}
            />
          </div>

          <div className="min-w-0 flex-1 overflow-x-auto">
            <SchedulingGrid
              weekStart={weekStartDate}
              dispatchCenterId={selectedDispatchCenterId}
              dispatchCenterName={
                selectedDispatchCenter?.name ?? "Dispatch center"
              }
              operators={dispatchCenterOperators}
              operatorsLoading={operators.isLoading}
              operatorsError={operators.error}
              operatorsDataAvailable={operators.data !== undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchCenterSelect({
  dispatchCenters,
  selectedDispatchCenterId,
  onDispatchCenterChange,
}: {
  dispatchCenters: CarrierDispatchCenter[];
  selectedDispatchCenterId: string;
  onDispatchCenterChange: (value: string) => void;
}) {
  return (
    <Select
      value={selectedDispatchCenterId}
      onValueChange={onDispatchCenterChange}
    >
      <SelectTrigger size="sm" className="w-full sm:w-[200px]">
        <SelectValue placeholder="Select dispatch center" />
      </SelectTrigger>
      <SelectContent>
        {dispatchCenters
          .filter((center) => center.isActive)
          .map((center) => (
            <SelectItem key={center.id} value={center.id}>
              {center.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}

function WeekNavigator({
  weekStart,
  onChange,
}: {
  weekStart: Date;
  onChange: (date: Date) => void;
}) {
  const weekEnd = addDays(weekStart, 6);

  return (
    <div className="grid w-full grid-cols-[44px_1fr_44px] items-center gap-2 sm:flex sm:w-auto sm:gap-3">
      <Button
        variant="outline"
        size="icon-touch"
        onClick={() => onChange(subWeeks(weekStart, 1))}
        aria-label="Previous week"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="min-w-0 text-center">
        <p className="font-medium">
          {format(weekStart, "MMM d")} - {format(weekEnd, "MMM d, yyyy")}
        </p>
        <p className="text-sm text-gray-500">
          Week of {format(weekStart, "MMMM d")}
        </p>
      </div>
      <Button
        variant="outline"
        size="icon-touch"
        onClick={() => onChange(addWeeks(weekStart, 1))}
        aria-label="Next week"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="touch"
        className="col-span-3 sm:col-auto"
        onClick={() => onChange(startOfWeek(new Date(), { weekStartsOn: 1 }))}
      >
        Today
      </Button>
    </div>
  );
}

function RequestAvailabilityCard({
  weekStart,
  dispatchCenterId,
  dispatchCenterName,
  operators,
  operatorsLoading,
}: {
  weekStart: Date;
  dispatchCenterId: string;
  dispatchCenterName: string;
  operators: CarrierOperator[];
  operatorsLoading: boolean;
}) {
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedOperatorIds, setSelectedOperatorIds] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const requestLock = useRef(false);
  const weekStartStr = format(weekStart, "yyyy-MM-dd");

  const requestsQuery = useCarrierSchedulingRequests({
    dispatchCenterId,
    weekStart: weekStartStr,
    limit: 5,
    offset: 0,
  });
  const { data: requests, isLoading, error: requestsError } = requestsQuery;
  const createRequest = useCreateAvailabilityRequest();
  const updateStatus = useUpdateAvailabilityRequestStatus();
  const sendReminder = useRemindAvailabilityRequest();
  const requestScopeKey = `${dispatchCenterId}:${weekStartStr}`;

  useEffect(() => {
    if (!requestScopeKey) return;
    setSelectedOperatorIds([]);
    setMessage("");
  }, [requestScopeKey]);

  const existingRequest = requests?.data?.[0];

  const createSelectedRequest = () => {
    if (requestLock.current || selectedOperatorIds.length === 0) return;
    requestLock.current = true;
    setSending(true);
    setRequestError(null);
    createRequest.mutate(
      {
        dispatchCenterId,
        weekStart: weekStartStr,
        message: message.trim() || undefined,
        operatorIds:
          selectedOperatorIds.length > 0 ? selectedOperatorIds : undefined,
      },
      {
        onSuccess: () => {
          toast.success("Availability request sent to operators.");
          setMessage("");
          setSelectedOperatorIds([]);
        },
        onError: (caught) => {
          const nextError = hasHttpStatus(caught, 403)
            ? "You do not have permission to request availability."
            : "Availability request could not be sent. Please retry.";
          setRequestError(nextError);
          toast.error(nextError);
        },
        onSettled: () => {
          requestLock.current = false;
          setSending(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent
          role="status"
          aria-label="Loading availability requests"
          className="flex justify-center p-6"
        >
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (existingRequest) {
    return (
      <div className="space-y-2">
        {requestsError && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
          >
            Availability request status could not be refreshed. Cached request
            details remain visible.
          </div>
        )}
        <ExistingRequestCard
          request={existingRequest}
          dispatchCenterName={dispatchCenterName}
          updateStatus={updateStatus}
          sendReminder={sendReminder}
        />
      </div>
    );
  }

  if (requestsError) {
    return (
      <Card>
        <CardContent role="alert" className="space-y-3 p-4 text-sm">
          <p className="text-destructive">
            {hasHttpStatus(requestsError, 403)
              ? "You do not have permission to view availability requests."
              : "Availability requests could not be loaded."}
          </p>
          {!hasHttpStatus(requestsError, 403) && (
            <Button
              type="button"
              variant="outline"
              size="touch"
              onClick={() => requestsQuery.refetch()}
            >
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const toggleOperator = (operatorId: string) => {
    setSelectedOperatorIds((current) =>
      current.includes(operatorId)
        ? current.filter((id) => id !== operatorId)
        : [...current, operatorId]
    );
  };

  const selectAll = () => {
    setSelectedOperatorIds(operators.map((operator) => operator.id));
  };

  const requestForm = (idPrefix: string) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Users className="h-4 w-4" />
            Operators ({selectedOperatorIds.length}/{operators.length})
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm-touch"
              className="min-w-11 px-2"
              onClick={selectAll}
              disabled={operatorsLoading || sending}
            >
              All
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm-touch"
              className="min-w-11 px-2"
              onClick={() => setSelectedOperatorIds([])}
              disabled={operatorsLoading || sending}
            >
              None
            </Button>
          </div>
        </div>
        <div className="max-h-56 overflow-auto rounded-md border p-2 md:h-32">
          {operatorsLoading ? (
            <div className="flex items-center justify-center py-6 text-sm text-gray-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading operators
            </div>
          ) : operators.length === 0 ? (
            <div className="py-2 text-center text-sm text-gray-400">
              No operators in this dispatch center
            </div>
          ) : (
            <div className="space-y-2">
              {operators.map((operator) => {
                const checkboxId = `${idPrefix}-operator-${operator.id}`;
                return (
                  <label
                    key={operator.id}
                    htmlFor={checkboxId}
                    className="flex min-h-11 cursor-pointer items-center gap-2 text-sm md:min-h-0"
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={selectedOperatorIds.includes(operator.id)}
                      onCheckedChange={() => toggleOperator(operator.id)}
                      disabled={sending}
                    />
                    <span>{operatorName(operator)}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label
          htmlFor={`${idPrefix}-availability-message`}
          className="text-sm font-medium"
        >
          Message (optional)
        </label>
        <Textarea
          id={`${idPrefix}-availability-message`}
          rows={3}
          placeholder="Any notes for operators..."
          value={message}
          onChange={(event) => {
            setMessage(event.target.value);
            setRequestError(null);
          }}
          disabled={sending}
        />
      </div>

      {requestError && (
        <p role="alert" className="text-sm text-destructive">
          {requestError}
        </p>
      )}
      {sending && (
        <p role="status" className="text-sm text-muted-foreground">
          Sending availability request…
        </p>
      )}

      <div className="sticky bottom-0 border-t bg-background pt-3">
        <Button
          type="button"
          size="touch"
          onClick={createSelectedRequest}
          disabled={
            sending || operatorsLoading || selectedOperatorIds.length === 0
          }
          className="h-11 w-full"
        >
          {sending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send to {selectedOperatorIds.length} Operator
              {selectedOperatorIds.length === 1 ? "" : "s"}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Card className="relative md:hidden">
        <Button
          type="button"
          variant="ghost"
          aria-label="Request operator availability"
          className="absolute inset-0 h-full w-full rounded-xl p-0"
          onClick={() => setMobileEditorOpen(true)}
        />
        <CardContent className="pointer-events-none relative flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Request Availability</p>
            <p className="truncate text-xs text-muted-foreground">
              {format(weekStart, "MMM d, yyyy")} · {operators.length} operators
            </p>
          </div>
          <Send className="h-4 w-4 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>

      <DetailDrawer
        open={mobileEditorOpen}
        onOpenChange={(open) => {
          if (!sending) setMobileEditorOpen(open);
        }}
        title="Request Availability"
        description={`Week of ${format(weekStart, "MMM d, yyyy")} at ${dispatchCenterName}`}
      >
        {requestForm("mobile")}
      </DetailDrawer>

      <Card className="hidden md:block">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Request Availability</CardTitle>
          <CardDescription>
            Week of {format(weekStart, "MMM d, yyyy")} at {dispatchCenterName}
          </CardDescription>
        </CardHeader>
        <CardContent>{requestForm("desktop")}</CardContent>
      </Card>
    </>
  );
}

function ExistingRequestCard({
  request,
  dispatchCenterName,
  updateStatus,
  sendReminder,
}: {
  request: CarrierAvailabilityRequest;
  dispatchCenterName: string;
  updateStatus: ReturnType<typeof useUpdateAvailabilityRequestStatus>;
  sendReminder: ReturnType<typeof useRemindAvailabilityRequest>;
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const operators = request.targetOperators?.length
    ? request.targetOperators
    : request.operators;
  const respondedOperators = operators.filter((operator) => operator.submitted);
  const pendingOperators = operators.filter((operator) => !operator.submitted);
  const status = request.status ?? "OPEN";

  const handleStatusChange = (nextStatus: string) => {
    setActionError(null);
    updateStatus.mutate(
      { requestId: request.id, status: nextStatus },
      {
        onSuccess: () => {
          const label = humanizeShift(nextStatus).toLowerCase();
          toast.success(`Availability request ${label}.`);
        },
        onError: () => {
          setActionError("Availability request status could not be updated.");
          toast.error("Failed to update request status.");
        },
      }
    );
  };

  const handleReminder = () => {
    setActionError(null);
    sendReminder.mutate(request.id, {
      onSuccess: (result) => {
        if (result.remindedCount === 0) {
          toast.info("All operators have already submitted.");
        } else {
          toast.success(
            `Reminder sent to ${result.remindedCount} operator${result.remindedCount === 1 ? "" : "s"}.`
          );
        }
      },
      onError: () => {
        setActionError("Reminder could not be sent. Please retry.");
        toast.error("Failed to send reminder.");
      },
    });
  };

  return (
    <>
      <Card className="relative md:hidden">
        <Button
          type="button"
          variant="ghost"
          aria-label="Review availability request"
          className="absolute inset-0 h-full w-full rounded-xl p-0"
          onClick={() => setDetailOpen(true)}
        />
        <CardContent className="pointer-events-none relative flex items-center justify-between gap-3 p-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium">Request Sent</p>
              <StatusBadge status={status} />
            </div>
            <p className="text-xs text-muted-foreground">
              {request.submittedCount}/{request.totalOperators} submitted
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </CardContent>
      </Card>

      <DetailDrawer
        open={detailOpen}
        onOpenChange={setDetailOpen}
        title="Availability Request"
        description={`Week of ${formatCalendarDate(request.weekStart)} · ${dispatchCenterName}`}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <StatusBadge status={status} />
            <span className="text-xs text-muted-foreground">
              Sent {format(new Date(request.createdAt), "MMM d 'at' h:mm a")}
            </span>
          </div>
          {request.message && (
            <div className="rounded-md bg-muted p-3 text-sm italic">
              &quot;{request.message}&quot;
            </div>
          )}
          <p className="text-sm">
            <span className="font-medium text-green-600">
              {request.submittedCount}
            </span>{" "}
            <span className="text-muted-foreground">
              / {request.totalOperators} submitted
            </span>
          </p>
          <div className="max-h-72 overflow-y-auto rounded-md border p-2">
            {respondedOperators.map((operator) => (
              <OperatorRequestRow
                key={operator.id}
                operator={operator}
                submitted
              />
            ))}
            {pendingOperators.map((operator) => (
              <OperatorRequestRow key={operator.id} operator={operator} />
            ))}
          </div>
          {actionError && (
            <p role="alert" className="text-sm text-destructive">
              {actionError}
            </p>
          )}
          <div className="sticky bottom-0 grid gap-2 border-t bg-background pt-3">
            {status === "OPEN" ? (
              <>
                {pendingOperators.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="touch"
                    onClick={handleReminder}
                    disabled={sendReminder.isPending}
                  >
                    <Bell className="mr-2 h-4 w-4" />
                    Send reminder ({pendingOperators.length})
                  </Button>
                )}
                <Button
                  type="button"
                  size="touch"
                  onClick={() => handleStatusChange("CLOSED")}
                  disabled={updateStatus.isPending}
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Close request
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="touch"
                  onClick={() => handleStatusChange("CANCELLED")}
                  disabled={updateStatus.isPending}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel request
                </Button>
              </>
            ) : (
              <Button
                type="button"
                size="touch"
                onClick={() => handleStatusChange("OPEN")}
                disabled={updateStatus.isPending}
              >
                <Unlock className="mr-2 h-4 w-4" />
                Reopen request
              </Button>
            )}
          </div>
        </div>
      </DetailDrawer>

      <Card className="hidden md:block">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <Check className="h-5 w-5 shrink-0 text-green-600" />
              <CardTitle className="truncate text-lg">Request Sent</CardTitle>
              <StatusBadge status={status} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm-touch"
                  aria-label="Availability request actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {status === "OPEN" && (
                  <>
                    {pendingOperators.length > 0 && (
                      <DropdownMenuItem
                        onClick={handleReminder}
                        disabled={sendReminder.isPending}
                      >
                        <Bell className="mr-2 h-4 w-4" />
                        Send Reminder ({pendingOperators.length})
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleStatusChange("CLOSED")}
                      disabled={updateStatus.isPending}
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      Close Request
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleStatusChange("CANCELLED")}
                      disabled={updateStatus.isPending}
                      variant="destructive"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancel Request
                    </DropdownMenuItem>
                  </>
                )}
                {status !== "OPEN" && (
                  <DropdownMenuItem
                    onClick={() => handleStatusChange("OPEN")}
                    disabled={updateStatus.isPending}
                  >
                    <Unlock className="mr-2 h-4 w-4" />
                    Reopen Request
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <CardDescription>
            Sent on {format(new Date(request.createdAt), "MMM d 'at' h:mm a")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {actionError && (
            <p role="alert" className="text-sm text-destructive">
              {actionError}
            </p>
          )}
          {request.message && (
            <div className="rounded bg-gray-50 p-2 text-sm italic">
              "{request.message}"
            </div>
          )}

          <div className="text-sm">
            <span className="font-medium text-green-600">
              {request.submittedCount}
            </span>
            <span className="text-gray-500">
              {" "}
              / {request.totalOperators} submitted
            </span>
          </div>

          <div className="h-40 overflow-auto">
            <div className="space-y-1">
              {respondedOperators.map((operator) => (
                <OperatorRequestRow
                  key={operator.id}
                  operator={operator}
                  submitted
                />
              ))}
              {pendingOperators.map((operator) => (
                <OperatorRequestRow key={operator.id} operator={operator} />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function OperatorRequestRow({
  operator,
  submitted = false,
}: {
  operator: CarrierAvailabilityRequestOperator;
  submitted?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 py-1 text-sm",
        submitted ? "" : "text-gray-400"
      )}
    >
      {submitted ? (
        <CheckCircle2 className="h-4 w-4 text-green-600" />
      ) : (
        <Circle className="h-4 w-4" />
      )}
      <span>{operator.name}</span>
    </div>
  );
}

function SchedulingGrid({
  weekStart,
  dispatchCenterId,
  dispatchCenterName,
  operators,
  operatorsLoading,
  operatorsError,
  operatorsDataAvailable,
}: {
  weekStart: Date;
  dispatchCenterId: string;
  dispatchCenterName: string;
  operators: CarrierOperator[];
  operatorsLoading: boolean;
  operatorsError: unknown;
  operatorsDataAvailable: boolean;
}) {
  const weekStartStr = format(weekStart, "yyyy-MM-dd");
  const availability = useCarrierAvailability(dispatchCenterId, weekStartStr);
  const shifts = useCarrierShifts(dispatchCenterId, weekStartStr);
  const deleteShift = useDeleteCarrierShift();
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [dialogState, setDialogState] = useState<{
    row: ReturnType<typeof mergeSchedulingRows>[number];
    date: Date;
    initialShift?: ShiftType;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const deleteLock = useRef(false);

  const isLoading =
    operatorsLoading || availability.isLoading || shifts.isLoading;

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading schedule"
        className="flex justify-center p-8"
      >
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const rows = mergeSchedulingRows({
    operators,
    availabilityRows: availability.data ?? [],
    shiftRows: shifts.data ?? [],
  });
  const scheduleError = operatorsError ?? availability.error ?? shifts.error;
  const blockingScheduleError =
    (!operatorsDataAvailable && operatorsError) ||
    (availability.data === undefined && availability.error) ||
    (shifts.data === undefined && shifts.error);

  if (blockingScheduleError) {
    return (
      <ErrorMessage
        label="schedule"
        forbidden={hasHttpStatus(blockingScheduleError, 403)}
      />
    );
  }

  if (rows.length === 0) {
    if (scheduleError) {
      return (
        <ErrorMessage
          label="schedule"
          forbidden={hasHttpStatus(scheduleError, 403)}
        />
      );
    }
    return (
      <div className="py-8 text-center text-gray-500">
        No operators found for this dispatch center
      </div>
    );
  }

  const hasPlannedWork =
    (availability.data?.length ?? 0) > 0 ||
    rows.some((row) => row.shifts.length > 0);
  const selectedDate = addDays(weekStart, selectedDayIndex);

  const requestDelete = (shift: ScheduledShift, operatorName: string) => {
    setDialogState(null);
    setTimeout(
      () =>
        setDeleteTarget({
          id: shift.id,
          label: `${humanizeShift(shift.shiftType)} shift for ${operatorName}`,
        }),
      0
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleteLock.current) return;
    deleteLock.current = true;
    deleteShift.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success("Shift removed.");
        setDeleteTarget(null);
      },
      onError: () => toast.error("Failed to remove shift."),
      onSettled: () => {
        deleteLock.current = false;
      },
    });
  };

  return (
    <div className="space-y-3">
      {scheduleError && (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          {hasHttpStatus(scheduleError, 403)
            ? "You do not have permission to load part of this schedule. Available data remains visible."
            : "Part of this schedule could not be refreshed. Available data remains visible."}
        </div>
      )}

      {!hasPlannedWork && (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          No availability or assigned shifts in this period. Open an operator
          day to plan a shift.
        </div>
      )}

      <div className="hidden flex-wrap items-center gap-4 text-sm text-gray-600 md:flex">
        <span className="font-medium">Legend:</span>
        {SHIFT_TYPES.map((type) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={cn("h-3 w-3 rounded", SHIFT_CONFIG[type].bg)} />
            <span>{SHIFT_CONFIG[type].label}</span>
            <span className="text-xs text-gray-400">
              ({SHIFT_CONFIG[type].time})
            </span>
          </div>
        ))}
        <span className="text-gray-400">|</span>
        <div className="flex items-center gap-1.5">
          <div className="h-4 w-8 rounded border-2 border-dashed border-gray-300" />
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusBadge status="CONFIRMED" className="px-1.5 py-0 text-[10px]" />
          <span>Assigned shift status</span>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        <fieldset className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          <legend className="sr-only">Select schedule day</legend>
          {DAYS.map((day, index) => {
            const date = addDays(weekStart, index);
            const selected = index === selectedDayIndex;
            return (
              <Button
                key={day.key}
                type="button"
                variant={selected ? "default" : "outline"}
                size="sm-touch"
                aria-pressed={selected}
                className="min-w-16 shrink-0 px-2"
                onClick={() => setSelectedDayIndex(index)}
              >
                <span className="text-xs">
                  {day.label} {format(date, "d")}
                </span>
              </Button>
            );
          })}
        </fieldset>

        <p className="text-xs font-medium text-muted-foreground">
          {format(selectedDate, "EEEE, MMMM d")}
        </p>

        <div className="space-y-2">
          {rows.map((row) => {
            const day = DAYS[selectedDayIndex];
            const dayAvailability = availabilityForDay(row.availability, day);
            const dayShifts = shiftsForDay(row.shifts, selectedDate);
            const unmatchedDayShifts = dayShifts.filter(
              (shift) => !normalizeShiftType(shift.shiftType)
            );
            const unmatchedDayAvailability = dayAvailability.filter(
              (slot) => !normalizeShiftType(slot)
            );
            return (
              <article
                key={row.operatorId}
                className="relative rounded-md border bg-background p-3"
              >
                <Button
                  type="button"
                  variant="ghost"
                  aria-label={`Review ${row.name} on ${format(selectedDate, "MMMM d")}`}
                  className="absolute inset-0 z-0 h-full w-full rounded-md p-0"
                  onClick={() => setDialogState({ row, date: selectedDate })}
                />
                <div className="pointer-events-none relative z-10">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-medium">
                        {row.name}
                      </h3>
                      <p className="truncate text-[11px] text-muted-foreground">
                        {row.notes ?? "No availability note"}
                      </p>
                    </div>
                    <span className="text-[11px] text-muted-foreground">
                      {dayShifts.length} assigned
                    </span>
                  </div>
                  <div className="mt-2 divide-y rounded-md border px-2">
                    {SHIFT_TYPES.map((shiftType) => {
                      const assigned = dayShifts.find(
                        (shift) =>
                          normalizeShiftType(shift.shiftType) === shiftType
                      );
                      const available = dayAvailability.some(
                        (slot) => normalizeShiftType(slot) === shiftType
                      );
                      return (
                        <div
                          key={shiftType}
                          className="flex min-h-8 items-center justify-between gap-2 py-1 text-xs"
                        >
                          <span>{SHIFT_CONFIG[shiftType].label}</span>
                          {assigned ? (
                            <span className="flex min-w-0 items-center gap-1.5 text-right">
                              <span className="truncate font-medium text-foreground">
                                {assigned.startTime ?? "No start"}–
                                {assigned.endTime ?? "No end"}
                              </span>
                              <StatusBadge
                                status={assigned.status}
                                className="shrink-0 px-1.5 py-0 text-[10px]"
                              />
                            </span>
                          ) : (
                            <span className="truncate text-right text-muted-foreground">
                              {available ? "Available" : "Open"}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {unmatchedDayShifts.map((shift) => (
                      <div
                        key={shift.id}
                        className="flex min-h-8 items-center justify-between gap-2 py-1 text-xs"
                      >
                        <span>{humanizeShift(shift.shiftType)}</span>
                        <span className="flex min-w-0 items-center gap-1.5 text-right">
                          <span className="truncate font-medium">
                            {shift.startTime ?? "No start"}–
                            {shift.endTime ?? "No end"}
                          </span>
                          <StatusBadge
                            status={shift.status}
                            className="shrink-0 px-1.5 py-0 text-[10px]"
                          />
                        </span>
                      </div>
                    ))}
                    {unmatchedDayAvailability.map((slot) => (
                      <div
                        key={`custom-availability-${slot}`}
                        className="flex min-h-8 items-center justify-between gap-2 py-1 text-xs"
                      >
                        <span>{humanizeShift(slot)}</span>
                        <span className="text-muted-foreground">Available</span>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 z-10 w-44 whitespace-nowrap bg-white">
                Operator
              </TableHead>
              {DAYS.map((day, index) => {
                const date = addDays(weekStart, index);
                const isToday =
                  format(date, "yyyy-MM-dd") ===
                  format(new Date(), "yyyy-MM-dd");
                return (
                  <TableHead
                    key={day.key}
                    className={cn(
                      "min-w-[120px] whitespace-nowrap text-center",
                      isToday && "bg-blue-50"
                    )}
                  >
                    <div className={isToday ? "font-bold text-blue-600" : ""}>
                      {format(date, "EEE")}
                    </div>
                    <div
                      className={cn(
                        "text-xs",
                        isToday ? "text-blue-600" : "text-gray-500"
                      )}
                    >
                      {format(date, "MMM d")}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.operatorId}>
                <TableCell className="sticky left-0 z-10 bg-white font-medium">
                  <div>{row.name}</div>
                  {row.notes && (
                    <div className="max-w-[140px] truncate text-xs text-gray-400">
                      {row.notes}
                    </div>
                  )}
                </TableCell>
                {DAYS.map((day, dayIndex) => {
                  const date = addDays(weekStart, dayIndex);
                  const isToday =
                    format(date, "yyyy-MM-dd") ===
                    format(new Date(), "yyyy-MM-dd");
                  const dayAvailability = availabilityForDay(
                    row.availability,
                    day
                  );
                  const dayShifts = shiftsForDay(row.shifts, date);
                  const unmatchedAvailability = dayAvailability.filter(
                    (slot) => !normalizeShiftType(slot)
                  );
                  const unmatchedShifts = dayShifts.filter(
                    (shift) => !normalizeShiftType(shift.shiftType)
                  );

                  return (
                    <TableCell
                      key={day.key}
                      className={cn("p-1.5", isToday && "bg-blue-50")}
                    >
                      <div className="flex flex-col gap-1">
                        {SHIFT_TYPES.map((shiftType) => {
                          const isAvailable = dayAvailability.some(
                            (slot) => normalizeShiftType(slot) === shiftType
                          );
                          const assignedShift = dayShifts.find(
                            (shift) =>
                              normalizeShiftType(shift.shiftType) === shiftType
                          );
                          const config = SHIFT_CONFIG[shiftType];

                          if (assignedShift) {
                            return (
                              <div
                                key={shiftType}
                                className={cn(
                                  "rounded border px-2 py-1 text-xs",
                                  config.border,
                                  config.bgLight
                                )}
                              >
                                <div className="flex items-center justify-between gap-1">
                                  <span
                                    className={cn("font-medium", config.text)}
                                  >
                                    {config.short}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon-xs-touch"
                                    onClick={() =>
                                      setDeleteTarget({
                                        id: assignedShift.id,
                                        label: `${config.label} shift for ${row.name}`,
                                      })
                                    }
                                    className={cn("shrink-0", config.text)}
                                    disabled={deleteShift.isPending}
                                    aria-label={`Remove ${config.label} shift for ${row.name} on ${format(date, "MMMM d")}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                                <div className="mt-1 flex items-center justify-between gap-1">
                                  {assignedShift.startTime ? (
                                    <span className="text-[10px] text-muted-foreground">
                                      {assignedShift.startTime}
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-muted-foreground">
                                      No start time
                                    </span>
                                  )}
                                  <StatusBadge
                                    status={assignedShift.status}
                                    className="px-1.5 py-0 text-[10px]"
                                  />
                                </div>
                              </div>
                            );
                          }

                          if (isAvailable) {
                            return (
                              <Button
                                key={shiftType}
                                type="button"
                                variant="outline"
                                size="xs"
                                onClick={() =>
                                  setDialogState({
                                    row,
                                    date,
                                    initialShift: shiftType,
                                  })
                                }
                                className={cn(
                                  "h-7 w-full justify-center border-2 border-dashed px-2 py-1 text-xs shadow-none hover:border-solid",
                                  config.border,
                                  config.bgLight,
                                  config.text
                                )}
                                aria-label={`Assign ${config.label} shift to ${row.name} on ${format(date, "MMMM d")}`}
                              >
                                <Plus className="mr-1 h-3 w-3" />
                                <span>{config.short}</span>
                              </Button>
                            );
                          }

                          return null;
                        })}

                        {unmatchedShifts.map((shift) => (
                          <div
                            key={shift.id}
                            className="rounded border bg-muted px-2 py-1 text-xs"
                          >
                            <div className="flex items-center justify-between gap-1">
                              <span>{humanizeShift(shift.shiftType)}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs-touch"
                                onClick={() =>
                                  setDeleteTarget({
                                    id: shift.id,
                                    label: `${humanizeShift(shift.shiftType)} shift for ${row.name}`,
                                  })
                                }
                                disabled={deleteShift.isPending}
                                aria-label={`Remove ${humanizeShift(shift.shiftType)} shift for ${row.name} on ${format(date, "MMMM d")}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                            <StatusBadge
                              status={shift.status}
                              className="mt-1 px-1.5 py-0 text-[10px]"
                            />
                          </div>
                        ))}

                        {unmatchedAvailability.map((slot) => (
                          <div
                            key={slot}
                            className="rounded border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-center text-xs text-gray-600"
                          >
                            {humanizeShift(slot)}
                          </div>
                        ))}

                        {dayAvailability.length === 0 &&
                          dayShifts.length === 0 && (
                            <Button
                              variant="ghost"
                              size="xs"
                              className="h-7 text-gray-400 hover:text-gray-600"
                              onClick={() =>
                                setDialogState({
                                  row,
                                  date,
                                })
                              }
                            >
                              <Plus className="mr-1 h-3 w-3" />
                              Add
                            </Button>
                          )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ScheduleDayDrawer
        key={
          dialogState
            ? `${dialogState.row.operatorId}:${format(dialogState.date, "yyyy-MM-dd")}:${dialogState.initialShift ?? "overview"}`
            : "closed"
        }
        state={dialogState}
        dispatchCenterId={dispatchCenterId}
        dispatchCenterName={dispatchCenterName}
        onOpenChange={(open) => {
          if (!open) setDialogState(null);
        }}
        onRequestDelete={requestDelete}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteShift.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove assigned shift?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `${deleteTarget.label} will be removed from this schedule.`
                : "This shift will be removed."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteShift.isPending}>
              Keep shift
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteShift.isPending}
              onClick={(event) => {
                event.preventDefault();
                confirmDelete();
              }}
            >
              {deleteShift.isPending ? "Removing…" : "Remove shift"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ScheduleDayDrawer({
  state,
  onOpenChange,
  dispatchCenterId,
  dispatchCenterName,
  onRequestDelete,
}: {
  state: {
    row: ReturnType<typeof mergeSchedulingRows>[number];
    date: Date;
    initialShift?: ShiftType;
  } | null;
  onOpenChange: (open: boolean) => void;
  dispatchCenterId: string;
  dispatchCenterName: string;
  onRequestDelete: (shift: ScheduledShift, operatorName: string) => void;
}) {
  const createShift = useCreateCarrierShift();
  const initialShift = state?.initialShift ?? null;
  const defaultShift = initialShift ?? "MORNING";
  const [selectedShift, setSelectedShift] = useState<ShiftType | null>(
    initialShift
  );
  const [shiftType, setShiftType] = useState<ShiftType>(defaultShift);
  const [startTime, setStartTime] = useState(
    SHIFT_CONFIG[defaultShift].defaultStart
  );
  const [endTime, setEndTime] = useState(SHIFT_CONFIG[defaultShift].defaultEnd);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitLock = useRef(false);

  const row = state?.row;
  const date = state?.date;
  const day = date ? DAYS[(date.getDay() + 6) % 7] : DAYS[0];
  const availability = row ? availabilityForDay(row.availability, day) : [];
  const assignedShifts = row && date ? shiftsForDay(row.shifts, date) : [];
  const unmatchedAssignments = assignedShifts.filter(
    (shift) => !normalizeShiftType(shift.shiftType)
  );
  const unmatchedAvailability = availability.filter(
    (slot) => !normalizeShiftType(slot)
  );
  const selectedAssignment = selectedShift
    ? assignedShifts.find(
        (shift) => normalizeShiftType(shift.shiftType) === selectedShift
      )
    : undefined;

  const handleShiftTypeChange = (value: ShiftType) => {
    setSelectedShift(value);
    setShiftType(value);
    setStartTime(SHIFT_CONFIG[value].defaultStart);
    setEndTime(SHIFT_CONFIG[value].defaultEnd);
    setError(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!state || !date || submitLock.current) return;
    submitLock.current = true;
    setSaving(true);
    setError(null);
    createShift.mutate(
      {
        operatorId: state.row.operatorId,
        dispatchCenterId,
        date: format(date, "yyyy-MM-dd"),
        shiftType,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        notes: notes.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Shift assigned.");
          onOpenChange(false);
        },
        onError: (caught) => {
          const message = hasHttpStatus(caught, 403)
            ? "You do not have permission to assign this shift."
            : hasHttpStatus(caught, 409)
              ? "This operator already has a conflicting shift for that time."
              : "Shift could not be assigned. Please retry.";
          setError(message);
          toast.error(message);
        },
        onSettled: () => {
          submitLock.current = false;
          setSaving(false);
        },
      }
    );
  };

  return (
    <DetailDrawer
      open={state !== null}
      onOpenChange={(open) => {
        if (!open && !saving) onOpenChange(false);
      }}
      title={row && date ? `${row.name} · ${format(date, "EEE, MMM d")}` : ""}
      description={dispatchCenterName}
      widthClassName="sm:max-w-lg"
    >
      {row && date && (
        <div className="space-y-4">
          <dl className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Operator</dt>
            <dd className="break-words">{row.name}</dd>
            <dt className="text-muted-foreground">Dispatch Center</dt>
            <dd className="break-words">{dispatchCenterName}</dd>
            <dt className="text-muted-foreground">Date</dt>
            <dd>{format(date, "EEEE, MMMM d, yyyy")}</dd>
            <dt className="text-muted-foreground">Availability note</dt>
            <dd className="break-words">
              {row.notes ?? "No availability note"}
            </dd>
          </dl>

          {selectedShift === null ? (
            <section className="space-y-2 border-t pt-4">
              <h3 className="text-sm font-semibold">Day plan</h3>
              {SHIFT_TYPES.map((type) => {
                const assignment = assignedShifts.find(
                  (shift) => normalizeShiftType(shift.shiftType) === type
                );
                const isAvailable = availability.some(
                  (slot) => normalizeShiftType(slot) === type
                );
                return (
                  <div key={type} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-2 text-sm">
                      <div>
                        <p className="font-medium">
                          {SHIFT_CONFIG[type].label}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {SHIFT_CONFIG[type].time}
                        </p>
                      </div>
                      {assignment ? (
                        <StatusBadge status={assignment.status} />
                      ) : (
                        <StatusBadge
                          status={isAvailable ? "AVAILABLE" : "OPEN"}
                        />
                      )}
                    </div>
                    <Button
                      type="button"
                      variant={assignment ? "outline" : "default"}
                      size="touch"
                      className="mt-3 w-full"
                      onClick={() => {
                        setSelectedShift(type);
                        setShiftType(type);
                        setStartTime(
                          assignment?.startTime ??
                            SHIFT_CONFIG[type].defaultStart
                        );
                        setEndTime(
                          assignment?.endTime ?? SHIFT_CONFIG[type].defaultEnd
                        );
                        setNotes(assignment?.notes ?? "");
                      }}
                    >
                      {assignment
                        ? `Review ${SHIFT_CONFIG[type].label.toLowerCase()} assignment`
                        : `Assign ${SHIFT_CONFIG[type].label.toLowerCase()} shift`}
                    </Button>
                  </div>
                );
              })}
              {unmatchedAssignments.map((assignment) => (
                <div
                  key={assignment.id}
                  className="space-y-3 rounded-md border p-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">
                        {humanizeShift(assignment.shiftType)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {assignment.startTime ?? "No start time"} –{" "}
                        {assignment.endTime ?? "No end time"}
                      </p>
                    </div>
                    <StatusBadge status={assignment.status} />
                  </div>
                  <p className="break-words text-xs text-muted-foreground">
                    {assignment.notes ?? "No notes"}
                  </p>
                  <Button
                    type="button"
                    variant="destructive"
                    size="touch"
                    className="w-full"
                    onClick={() => onRequestDelete(assignment, row.name)}
                  >
                    Remove {humanizeShift(assignment.shiftType).toLowerCase()}{" "}
                    shift
                  </Button>
                </div>
              ))}
              {unmatchedAvailability.map((slot) => (
                <div
                  key={`custom-availability-${slot}`}
                  className="flex items-center justify-between gap-2 rounded-md border p-3 text-sm"
                >
                  <span>{humanizeShift(slot)}</span>
                  <StatusBadge status="AVAILABLE" />
                </div>
              ))}
            </section>
          ) : selectedAssignment ? (
            <section className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-semibold">
                  {SHIFT_CONFIG[selectedShift].label} assignment
                </h3>
                <StatusBadge status={selectedAssignment.status} />
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-muted-foreground">Start</dt>
                <dd>{selectedAssignment.startTime ?? "No start time"}</dd>
                <dt className="text-muted-foreground">End</dt>
                <dd>{selectedAssignment.endTime ?? "No end time"}</dd>
                <dt className="text-muted-foreground">Notes</dt>
                <dd className="break-words">
                  {selectedAssignment.notes ?? "No notes"}
                </dd>
              </dl>
              <div className="sticky bottom-0 grid gap-2 border-t bg-background py-3 sm:grid-cols-2">
                <Button
                  type="button"
                  variant="outline"
                  size="touch"
                  onClick={() => setSelectedShift(null)}
                >
                  Back to day
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="touch"
                  onClick={() => onRequestDelete(selectedAssignment, row.name)}
                >
                  Remove shift
                </Button>
              </div>
            </section>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
              <p className="text-sm text-muted-foreground">
                Assign a shift to {row.name} on {format(date, "EEEE, MMMM d")}.
              </p>
              <div className="space-y-2">
                <label htmlFor="shift-type" className="text-sm font-medium">
                  Shift Type
                </label>
                <NativeSelect
                  id="shift-type"
                  value={shiftType}
                  onChange={(event) =>
                    handleShiftTypeChange(event.target.value as ShiftType)
                  }
                  disabled={saving}
                >
                  {SHIFT_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {SHIFT_CONFIG[type].label} ({SHIFT_CONFIG[type].time})
                    </option>
                  ))}
                </NativeSelect>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label htmlFor="shift-start" className="text-sm font-medium">
                    Start Time (optional)
                  </label>
                  <Input
                    id="shift-start"
                    type="time"
                    value={startTime}
                    onChange={(event) => {
                      setStartTime(event.target.value);
                      setError(null);
                    }}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="shift-end" className="text-sm font-medium">
                    End Time (optional)
                  </label>
                  <Input
                    id="shift-end"
                    type="time"
                    value={endTime}
                    onChange={(event) => {
                      setEndTime(event.target.value);
                      setError(null);
                    }}
                    disabled={saving}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="shift-notes" className="text-sm font-medium">
                  Notes (optional)
                </label>
                <Textarea
                  id="shift-notes"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={4}
                  placeholder="Any special instructions..."
                  disabled={saving}
                />
              </div>

              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              {saving && (
                <p role="status" className="text-sm text-muted-foreground">
                  Assigning shift…
                </p>
              )}

              <div className="sticky bottom-0 grid gap-2 border-t bg-background py-3 sm:grid-cols-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="touch"
                  onClick={() => setSelectedShift(null)}
                  disabled={saving}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="touch"
                  onClick={() => onOpenChange(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" size="touch" disabled={saving}>
                  {saving ? "Assigning…" : "Assign Shift"}
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}

function mergeSchedulingRows({
  operators,
  availabilityRows,
  shiftRows,
}: {
  operators: CarrierOperator[];
  availabilityRows: CarrierAvailabilityRow[];
  shiftRows: CarrierShiftRow[];
}) {
  const operatorIds = new Set<string>([
    ...operators.map((operator) => operator.id),
    ...availabilityRows.map((row) => row.operatorId),
    ...shiftRows.map((row) => row.operatorId),
  ]);

  return Array.from(operatorIds).map((operatorId) => {
    const operator = operators.find((item) => item.id === operatorId);
    const availability = availabilityRows.find(
      (item) => item.operatorId === operatorId
    );
    const shifts = shiftRows.find((item) => item.operatorId === operatorId);

    return {
      operatorId,
      name: availability?.name ?? shifts?.name ?? operatorName(operator),
      availability: availability?.availability ?? null,
      notes: availability?.notes ?? null,
      shifts: shifts?.shifts ?? operator?.shifts ?? [],
    };
  });
}

function shiftsForDay(shifts: CarrierShiftRow["shifts"], date: Date) {
  const targetDate = format(date, "yyyy-MM-dd");
  return shifts.filter((shift) => shift.date.startsWith(targetDate));
}

function availabilityForDay(
  value: unknown,
  day: (typeof DAYS)[number]
): string[] {
  if (!isRecord(value)) return [];
  const aliasValue = day.aliases
    .map((alias) => value[alias] ?? value[alias.toUpperCase()])
    .find((item) => item != null);
  const raw = value[day.key] ?? value[day.key.toUpperCase()] ?? aliasValue;
  return normalizeAvailabilityCell(raw);
}

function normalizeAvailabilityCell(value: unknown): string[] {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((item) => normalizeAvailabilityCell(item));
  }
  if (typeof value === "string") return [value];
  if (isRecord(value)) {
    if (typeof value.start === "string" && typeof value.end === "string") {
      return [`${value.start}-${value.end}`];
    }
    if (Array.isArray(value.shifts)) {
      return normalizeAvailabilityCell(value.shifts);
    }
    if (typeof value.label === "string") return [value.label];
  }
  return [String(value)];
}

function normalizeShiftType(value: string): ShiftType | null {
  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (normalized.startsWith("MORNING") || normalized === "MOR") {
    return "MORNING";
  }
  if (normalized.startsWith("AFTERNOON") || normalized === "AFT") {
    return "AFTERNOON";
  }
  if (normalized.startsWith("EVENING") || normalized === "EVE") {
    return "EVENING";
  }
  return null;
}

function humanizeShift(value: string): string {
  if (/^\d{2}:\d{2}/.test(value)) return value;
  return value
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

function operatorName(operator: CarrierOperator | undefined): string {
  if (!operator) return "Unknown";
  return operatorDisplayName(operator);
}

function formatCalendarDate(value: string) {
  const dateOnly = value.slice(0, 10);
  return format(new Date(`${dateOnly}T12:00:00`), "MMM d, yyyy");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function ErrorMessage({
  label,
  forbidden = false,
}: {
  label: string;
  forbidden?: boolean;
}) {
  return (
    <div role="alert" className="p-4 text-sm text-destructive">
      {forbidden
        ? `You do not have permission to load ${label}.`
        : `Failed to load ${label}.`}
    </div>
  );
}
